/**
 * The knobs of one layer's ordering, drawn from `orderingKnobs`.
 *
 * Blank means the default (the placeholder shows it), which is also what an
 * absent key means on the wire. Clearing a field removes the key.
 */

import { CanvasNumberInput } from "@/components/canvas-editor/components/Inputs";
import { orderingKnobs } from "@/components/canvas-editor/ordering-settings";
import { SimpleTooltip } from "@/components/ui/tooltip";
import type { LensOrdering } from "@/lib/lens";

export function OrderingSettings({
  order,
  values,
  onChange,
}: {
  order: LensOrdering;
  values: Record<string, unknown>;
  onChange: (key: string, value: number | undefined) => void;
}) {
  const knobs = orderingKnobs[order];
  if (knobs.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-2">
      {knobs.map((knob) => {
        const field = (
          <label className="flex min-w-[96px] flex-1 flex-col gap-0.5" style={{ flexBasis: "18%" }}>
            <span className="text-muted-foreground truncate px-0.5 text-[11px]">{knob.label}</span>
            <CanvasNumberInput
              aria-label={`${knob.label} for this layer`}
              placeholder={String(knob.default)}
              value={typeof values[knob.key] === "number" ? (values[knob.key] as number) : undefined}
              min={knob.min}
              step={knob.step}
              onValueChange={(v) => onChange(knob.key, Number.isFinite(v) ? v : undefined)}
            />
          </label>
        );
        return knob.hint ? (
          <SimpleTooltip key={knob.key} message={knob.hint}>
            {field}
          </SimpleTooltip>
        ) : (
          <div key={knob.key} className="contents">
            {field}
          </div>
        );
      })}
    </div>
  );
}
